import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Anchor,
  Center,
  Divider,
  Flex,
  Image,
  Modal as MantineModal,
  Stack,
  Text,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { Modal, ModalLoading, TagCapsules } from "../../components";
import PurchaseStep1 from "./PurchaseStep1";
import PurchaseStep2 from "./PurchaseStep2";
import PurchaseStep3 from "./PurchaseStep3";
import PurchaseStep4 from "./PurchaseStep4";
import HeaderModal from "./HeaderModal";
import { validationBuyNow } from "../../utils/validation/schemas/validationBuyNow";
import { IPostBuyNow } from "../../interfaces/PurchaseNow";
import PageNotFound from "../PageNotFound";
import { client_selected } from "../../client";
import { IColor } from "../../utils/selectColors";
import { postBuyNow, useProduct, useUserIP } from "../../hooks";
import { useTranslation } from "react-i18next";
import {
  tradutorCategory,
  tradutorSubcategory,
} from "../../utils/SubCategorys";

export default function PurchaseNow() {
  const { t } = useTranslation();
  const logo = client_selected.image.logo2;
  const { slug } = useParams();
  const id = slug?.split("id=")[1];
  const idArray = id ? [Number(id)] : [];

  const { data, isFetched, isError } = useProduct({ productId: idArray });
  const [opened, { close }] = useDisclosure(true);

  const navigate = useNavigate();

  const { data: dataIp } = useUserIP();
  const userIp = !!dataIp?.data.ip ? dataIp?.data.ip : "";
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [isOpenModalError, setIsOpenModalError] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openModalConfirmation, setOpenModalConfirmation] =
    useState<boolean>(false);

  const getDefaultValues: IPostBuyNow = {
    user: {
      name: "",
      document_nr: "",
      email: "",
      cell_phone: "",
      term_of_contract: false,
      client_ip: userIp,
    },
    payment_owner: {
      name: "",
      document_nr: "",
      email: "",
      address: {
        zip_code: "",
        city: "",
        state: "",
        additional_details: "",
        country: "BR",
        number: "",
        street: "",
        neighborhood: "",
      },
    },
    payment_info: null,
    plan_id: undefined,
    term_of_product: false,
    is_the_same_user: false,
    planHasPrice: undefined,
  };

  const methods = useForm<IPostBuyNow>({
    mode: "all",
    resolver: yupResolver(validationBuyNow),
    defaultValues: getDefaultValues,
  });

  const { reset, handleSubmit, watch, setValue } = methods;

  const price =
    !!data && data.plan.find((item) => item.id === watch("plan_id"))?.price;

  useEffect(() => {
    if (!!price) {
      setValue("planHasPrice", true);
    } else {
      setValue("planHasPrice", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [price]);
  const hasPrice = watch("planHasPrice");

  const { mutate: buyPlan } = useMutation(postBuyNow, {
    onSuccess: () => {
      setIsSuccess(true);
      setOpenModalConfirmation(true);
    },
    onError: (errors: any) => {
      console.error(t("marketplace.comprar_agora.error_buy"), errors);
      setIsSuccess(false);
      setIsOpenModalError(errors.response.data.message.toString() || "");
    },
  });

  async function onSubmit(data: IPostBuyNow) {
    const {
      is_the_same_user,
      planHasPrice,
      term_of_product,
      user: { term_of_contract, ...userData },
      ...restData
    } = data;
    setIsLoading(true);
    const newData = !!hasPrice
      ? { ...restData, user: userData }
      : { ...restData, user: userData, payment_info: null };
    buyPlan(newData);
  }

  const handleRedirect = () => {
    navigate("/autenticacao/acessar-conta");
  };

  const handleOnClose = () => {
    navigate("/marketplace");
    close();
    setIsLoading(false);
    setOpenModalConfirmation(false);
    reset();
  };

  const handleCloseConfirmation = () => {
    setOpenModalConfirmation(!openModalConfirmation);
  };

  const handleSucessConfirmationClose = () => {
    navigate("/marketplace");
    setIsLoading(false);
    setOpenModalConfirmation(!openModalConfirmation);
    close();
  };

  const handleCloseErrorModal = () => {
    setIsOpenModalError("");
    setIsLoading(false);
  };

  if (isError && isFetched && !data) return <PageNotFound />;

  return (
    <>
      {isLoading ? (
        <ModalLoading open={isLoading} />
      ) : (
        <Modal
          type={isSuccess ? "success" : "warning"}
          titleFirstButton={
            isSuccess
              ? t("marketplace.comprar_agora.modal_success.title_first_button")
              : t("marketplace.comprar_agora.modal_cancel.title_first_button")
          }
          titleSeccondButton={
            isSuccess
              ? t(
                  "marketplace.comprar_agora.modal_success.title_seccond_button"
                )
              : t("marketplace.comprar_agora.modal_cancel.title_seccond_button")
          }
          description={
            isSuccess
              ? t("marketplace.comprar_agora.modal_success.description")
              : t("marketplace.comprar_agora.modal_cancel.description")
          }
          open={openModalConfirmation}
          setClose={
            isSuccess ? handleSucessConfirmationClose : handleCloseConfirmation
          }
          secondClick={
            isSuccess ? handleSucessConfirmationClose : handleCloseConfirmation
          }
          firstClick={isSuccess ? handleRedirect : handleOnClose}
        />
      )}

      <Modal
        type={"danger"}
        titleFirstButton={t("global.button.close")}
        description={t("marketplace.comprar_agora.modal_error.description", {
          erro: isOpenModalError,
        })}
        contentEspecial={
          <Text
            c="basic.4"
            fz="16px"
            lh="155%"
            ta={"center"}
            mb="32px"
            maw={350}
          >
            {t("marketplace.comprar_agora.modal_error.content_special_1")}{" "}
            {!!client_selected?.email?.link ? (
              <Anchor href={client_selected.email.link} target="_blank">
                {t("marketplace.comprar_agora.modal_error.content_special_2")}
              </Anchor>
            ) : null}{" "}
            {t("marketplace.comprar_agora.modal_error.content_special_3")}
          </Text>
        }
        textModal={t("marketplace.comprar_agora.modal_error.text_modal")}
        open={!!isOpenModalError}
        setClose={handleCloseErrorModal}
        firstClick={handleCloseErrorModal}
      />
      <MantineModal
        yOffset={0}
        withCloseButton={false}
        opened={opened}
        onClose={handleOnClose}
        fullScreen
        closeOnEscape={false}
        transitionProps={{ transition: "slide-up", duration: 200 }}
        styles={{
          body: { padding: 0 },
          content: {
            background: "other.0",
          },
        }}
      >
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <HeaderModal
              title={
                <Flex
                  gap="24px"
                  align={"center"}
                  direction={{ base: "column", 991: "row" }}
                  justify={"center"}
                  wrap={"wrap"}
                >
                  <Center w={"250px"}>
                    <Image
                      src={logo}
                      alt="Logo"
                      fit="contain"
                      styles={{
                        root: {
                          cursor: "pointer",
                          transition: "0.3s",
                          ":hover": {
                            opacity: "0.9",
                            transition: "0.3s",
                          },
                        },
                      }}
                    />
                  </Center>
                  <Divider orientation="vertical" size="md" h-10 />
                  <Text
                    fw={600}
                    c="basic.5"
                    fz={32}
                    lh="48px"
                    style={{ whiteSpace: "nowrap" }}
                  >
                    {t("marketplace.comprar_agora.title")}
                  </Text>
                </Flex>
              }
              isLoading={isLoading}
              setCancelRegister={() =>
                setOpenModalConfirmation(!openModalConfirmation)
              }
            />
            <Center bg="other.0">
              <Flex
                justify="center"
                direction="column"
                maw={1920}
                p={{ base: "24px 24px", 991: "48px 48px", 1600: "48px 120px" }}
                w="100%"
                gap="80px"
              >
                <Flex
                  w="100%"
                  direction="column"
                  bg="basic.0"
                  style={{
                    borderRadius: "12px",
                    boxShadow: "0px 10px 40px rgba(0, 0, 0, 0.05)",
                  }}
                >
                  <Text
                    fw={600}
                    c="primary.2"
                    fz={32}
                    lh="31px"
                    p={{ base: "24px", 1600: "32px" }}
                  >
                    {t("marketplace.comprar_agora.your_product")}
                  </Text>
                  <Divider size="md" h-10 />
                  <Flex
                    p={{ base: "24px", 1600: "32px" }}
                    align="center"
                    gap="md"
                    w="100%"
                    justify={"space-between"}
                  >
                    <Flex gap="16px" align="center">
                      <Flex
                        w="60px"
                        h="60px"
                        sx={{
                          backgroundImage: `url(${data?.product?.icon})`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          borderRadius: "12px",
                        }}
                      />

                      <Stack spacing={4}>
                        <Flex gap="8px">
                          {data?.product?.subcategory ? (
                            <TagCapsules colorTag={IColor.orange} border>
                              {t(
                                tradutorCategory(data?.product?.category).title
                              )}
                            </TagCapsules>
                          ) : null}

                          {data?.product?.subcategory ? (
                            <TagCapsules colorTag={IColor.yellow} border>
                              {t(
                                tradutorSubcategory(data?.product?.subcategory)
                                  .title
                              )}
                            </TagCapsules>
                          ) : null}
                        </Flex>

                        <Text weight={700} size="xl" color="dark">
                          {data?.product?.title}
                        </Text>
                      </Stack>
                    </Flex>
                    <Stack spacing={0}>
                      <Text color="gray" size="sm">
                        {t("marketplace.comprar_agora.sell_by")}
                      </Text>
                      <Text color="dark" weight={600} size="sm">
                        {data?.product?.partner?.name || client_selected.name}
                      </Text>
                    </Stack>
                  </Flex>
                </Flex>
                <PurchaseStep1 />
                <PurchaseStep2 product={data} />
                <PurchaseStep3 hasPrice={!!hasPrice} />
                <PurchaseStep4
                  isLoading={isLoading}
                  hasPrice={!!hasPrice}
                  product={data}
                />
              </Flex>
            </Center>
          </form>
        </FormProvider>
      </MantineModal>
    </>
  );
}
